/**
 * BB-14d2c2 -- the product-facing audio lifecycle.
 *
 * Pure and dependency-free, like `mediaLegStates`: no React, no SIP.js,
 * no network. This module owns the single rule the whole slice exists
 * to enforce, so it can be tested without building a media leg.
 *
 * **The rule.** Campaign login succeeding no longer means the agent is
 * ready. Product Ready requires *both* a SIP session the browser
 * observed reach Established *and* the backend's own
 * `GET /agent-media/readiness` answering `ready: true`. Establishment
 * alone is not sufficient: the browser knows only that it sent an
 * INVITE and got a 200, which says nothing about whether the resulting
 * channel actually appeared in the agent's assigned conference. Only
 * the server-side observation can say that.
 *
 * **What ready still does not prove.** `ready: true` proves the
 * expected ephemeral SIP channel is present in the assigned ConfBridge
 * under the current lease generation. It does not prove bidirectional
 * RTP. Production RTP remains source-restricted, so arbitrary-browser
 * activation stays blocked on BB-14d3 regardless of what this module
 * reports.
 */

/**
 * Bounded product states. Deliberately three: the agent needs to know
 * that audio is being set up, that it is usable, or that it failed and
 * can be retried. Every finer distinction is a provider detail.
 */
export const AudioLifecycleState = Object.freeze({
  /** No campaign session. Nothing has been asked for. */
  IDLE: 'IDLE',
  /** Setting up audio. Start Dialing must not be available. */
  CONNECTING_AUDIO: 'CONNECTING_AUDIO',
  /** Audio is usable and the backend agrees. Start Dialing available. */
  READY: 'READY',
  /** Setup failed or audio was lost. Retry available, Start Dialing not. */
  AUDIO_FAILED: 'AUDIO_FAILED',
  /**
   * BB-14e. Audio reached READY and has since stopped being confirmed.
   *
   * Deliberately its own state rather than a return to CONNECTING_AUDIO.
   * The two look alike -- neither can dial -- but they mean opposite
   * things and warrant opposite remedies. CONNECTING_AUDIO is a leg that
   * has never worked, and it is bounded by a setup timeout that tears the
   * identity down. DEGRADED is a leg that demonstrably *did* work and may
   * simply be between confirmations; tearing it down would destroy the
   * agent's phone mid-shift, which is exactly the Production defect this
   * state exists to prevent.
   */
  DEGRADED: 'DEGRADED',
});

/**
 * The readiness vocabulary, mirrored from the backend's own
 * `BrowserReadinessReason`. Duplicated deliberately rather than
 * inferred: a reason the browser does not recognise must fall through
 * to "not ready", and that only works if the recognised set is written
 * down.
 */
export const ReadinessReason = Object.freeze({
  HEALTHY: 'healthy',
  NO_ACTIVE_IDENTITY: 'no_active_identity',
  CONFERENCE_EMPTY: 'conference_empty',
  OBSERVATION_STALE: 'observation_stale',
  LEASE_SUPERSEDED: 'lease_superseded',
  IDENTITY_EXPIRED: 'identity_expired',
  NO_SESSION: 'no_session',
  PROVIDER_UNAVAILABLE: 'provider_unavailable',
  NOT_READY: 'not_ready',
});

/**
 * Reasons that mean "this will not fix itself by waiting". Everything
 * else -- notably `observation_stale` and `conference_empty` -- is
 * transient while the next server-side heartbeat lands.
 */
const TERMINAL_REASONS = Object.freeze([
  ReadinessReason.LEASE_SUPERSEDED,
  ReadinessReason.IDENTITY_EXPIRED,
  ReadinessReason.NO_ACTIVE_IDENTITY,
  ReadinessReason.NO_SESSION,
]);

export function isTerminalReadinessReason(reason) {
  return TERMINAL_REASONS.includes(reason);
}

/**
 * `lease_superseded` is the one reason that must tear this controller
 * down rather than merely report failure: another tab holds the
 * session, and continuing to hold a SIP leg against a superseded
 * identity is exactly the duplicate-audio condition the lease exists to
 * prevent.
 */
export function requiresLocalTeardown(reason) {
  return reason === ReadinessReason.LEASE_SUPERSEDED;
}

/**
 * BB-14d2c3. The one degraded condition an agent can actually fix, and
 * currently cannot reach.
 *
 * A media identity that expired while its runtime lease stayed valid is
 * terminal server-side: renewal prevents expiry but never reverses it,
 * and `POST /bootstrap` is refused `already_active` while the expired
 * record is still ACTIVE at the current generation. The only way back
 * is the retry lifecycle -- dispose, scoped revoke, fresh bootstrap --
 * which the agent must be offered explicitly.
 *
 * Deliberately narrower than `DEGRADED`, and narrower than
 * `isTerminalReadinessReason`. `conference_empty` and
 * `observation_stale` resolve on the next server-side beat and must not
 * grow a Retry button they do not need; `lease_superseded` is handled
 * by `requiresLocalTeardown` and must never be offered as a retry,
 * because another tab holds the session. The two are separate
 * conditions and are kept separate.
 *
 * `DEGRADED` already implies the leg was healthy for this identity --
 * `deriveAudioLifecycleState` returns it only when `hasBeenReady` -- so
 * "after media had previously been ready" is structural here rather
 * than a second flag to keep in sync.
 */
export function requiresExplicitAudioRecovery({ audioState, readiness } = {}) {
  if (audioState !== AudioLifecycleState.DEGRADED) return false;
  if (!readiness || readiness.ready === true) return false;
  return readiness.reason === ReadinessReason.IDENTITY_EXPIRED;
}

/**
 * Poll cadence, in the authorized 3-5s band.
 *
 * Not faster: the answer is derived from a server-side observation the
 * S2 runtime refreshes on its own cadence, so polling below that
 * cadence asks the same question repeatedly and learns nothing.
 */
export const READINESS_POLL_MS = 4000;

/**
 * How long CONNECTING_AUDIO may last before it becomes AUDIO_FAILED.
 *
 * Derived, not chosen: readiness reflects the conference observation
 * written by the S2 session runtime, whose Logged In cadence is 15s
 * (`DEFAULT_LOGGED_IN_CADENCE_SECONDS`), and an observation older than
 * `TELEPHONY_BB14_MEDIA_OBSERVATION_MAX_AGE_SECONDS` (20s) is reported
 * as `observation_stale` rather than as health. A leg that joins the
 * conference immediately after a beat therefore cannot be confirmed for
 * up to a full cadence, and one lost beat pushes that to two.
 *
 * 45s is three Logged In cadences -- the same budget S2 itself allows
 * before declaring a session's beats failed
 * (`MAX_CONSECUTIVE_BEAT_FAILURES` = 3). Shorter would let the browser
 * declare failure while the backend still considered the session
 * healthy and recovering; longer would leave an agent watching a
 * spinner past the point the backend had already given up.
 */
export const CONNECTING_AUDIO_TIMEOUT_MS = 45000;

/**
 * Derive the product state.
 *
 * Fails closed in every direction: an unknown input, a missing
 * readiness answer, or a readiness answer whose `ready` is anything
 * other than exactly `true` all produce a non-ready state.
 *
 * @param {object} input
 * @param {boolean} input.sessionEstablished  Browser observed SIP Established.
 * @param {?object} input.readiness           Last `{ready, reason}` answer, or null.
 * @param {boolean} input.failed              A setup failure already occurred.
 * @param {boolean} input.started             A campaign session exists.
 */
export function deriveAudioLifecycleState({
  started = false,
  sessionEstablished = false,
  readiness = null,
  failed = false,
  hasBeenReady = false,
} = {}) {
  if (!started) {
    return AudioLifecycleState.IDLE;
  }
  if (failed) {
    return AudioLifecycleState.AUDIO_FAILED;
  }
  if (sessionEstablished && readiness && readiness.ready === true) {
    return AudioLifecycleState.READY;
  }
  // BB-14e. The latch is what makes this function no longer memoryless.
  // A media leg that has been confirmed healthy for *this* identity is
  // never described as "connecting" again -- the browser SIP leg is the
  // agent's phone and is meant to outlive every individual RATIO call.
  // Only replacing or tearing down the identity clears the latch.
  if (hasBeenReady) {
    return AudioLifecycleState.DEGRADED;
  }
  return AudioLifecycleState.CONNECTING_AUDIO;
}

/**
 * Start Dialing availability, defined once.
 *
 * The backend enforces this independently through the BB-14d2b Start
 * Dialing gate and the d2 admission fence; this is the product-side
 * half, and it must never be the only one. Weakening it would not
 * enable dialing -- it would only produce a button that fails.
 */
export function startDialingAvailable(audioState) {
  // Unchanged and deliberately strict: DEGRADED is not dialable either.
  // BB-14e widens how long a leg is *kept*, never what may dial.
  return audioState === AudioLifecycleState.READY;
}

/**
 * BB-14e -- may the initial establishment timeout be armed?
 *
 * `CONNECTING_AUDIO_TIMEOUT_MS` bounds *setting up* a leg that has never
 * worked. Applying it to an established leg is what revoked a working
 * agent's phone 45 seconds after a customer call ended. Defined here so
 * the rule has one home and the hook cannot re-derive it differently.
 */
export function establishmentTimeoutApplies(audioState) {
  return audioState === AudioLifecycleState.CONNECTING_AUDIO;
}
